import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Spin } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { empleadosApi } from '../../services/api';

const { Option } = Select;

const EmpleadoFormModal = ({ 
    visible, 
    onCancel, 
    onSuccess, 
    editingEmpleado = null,
    campus = [], 
    dependencias = [],
    tiposContratos = [],
    tiposEmpleados = []
}) => {
    const [form] = Form.useForm();
    const [loadingForm, setLoadingForm] = useState(false);
    
    // Reset form when modal opens or closes
    useEffect(() => {
        if (visible) {
            form.resetFields();
            if (editingEmpleado) {
                setLoadingForm(true);
                setTimeout(() => {
                    form.setFieldsValue({
                        nombre: editingEmpleado.nombre,
                        cedula: editingEmpleado.cedula,
                        cargo: editingEmpleado.cargo,
                        campus_id: (editingEmpleado.campus_id),
                        dependencia_id: (editingEmpleado.dependencia_id),
                        tipo_contrato_id: (editingEmpleado.tipo_contrato_id),
                        tipo_empleado_id: (editingEmpleado.tipo_empleado_id),
                        estado: editingEmpleado.estado,
                    });
                    setLoadingForm(false);
                }, 200);
            } else {
                form.setFieldsValue({
                    estado: "activo"
                });
            }
        }
    }, [visible, form, editingEmpleado]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            
            if (editingEmpleado) {
                // Actualizar empleado
                const empleadoData = {
                    nombre: values.nombre,
                    cedula: values.cedula,
                    cargo: values.cargo,
                    campus_id: values.campus_id,
                    dependencia_id: values.dependencia_id,
                    tipo_contrato_id: values.tipo_contrato_id,
                    tipo_empleado_id: values.tipo_empleado_id,
                    estado: values.estado,
                };
                await empleadosApi.update(editingEmpleado.id, empleadoData);
                
                toast.success(`Empleado ${values.nombre} actualizado correctamente`, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                // Crear nuevo empleado
                const empleadoData = {
                    nombre: values.nombre,
                    cedula: values.cedula,
                    cargo: values.cargo,
                    campus_id: values.campus_id,
                    dependencia_id: values.dependencia_id,
                    tipo_contrato_id: values.tipo_contrato_id,
                    tipo_empleado_id: values.tipo_empleado_id,
                    estado: values.estado,
                };
                await empleadosApi.create(empleadoData);
                
                toast.success(`Empleado ${values.nombre} creado correctamente`, {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
            
            onSuccess();
        } catch (error) {
            console.error("Error guardando empleado:", error);
            let errorMessage = "Error al guardar el empleado";
            
            if (error.response && error.response.data) {
                errorMessage = error.response.data.message || errorMessage;
            }
            
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
    };

    const modalTitle = (
        <div className="flex items-center">
            <UserOutlined className="text-xl text-blue-500 mr-2" />
            <span>
                {editingEmpleado 
                    ? `Editar Empleado - ${editingEmpleado.nombre}`
                    : "Crear Nuevo Empleado"}
            </span>
        </div>
    );

    return (
        <Modal
            title={modalTitle}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            width={700}
            okText={editingEmpleado ? "Actualizar" : "Crear"}
            cancelText="Cancelar"
            className="top-8"
            okButtonProps={{ className: "bg-blue-600 hover:bg-blue-700" }}
        >
            {loadingForm ? (
                <div className="flex justify-center items-center py-12">
                    <Spin tip="Cargando datos..." />
                </div>
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    name="empleadoForm"
                    className="[&_.ant-form-item]:mb-4"
                >
                    <Form.Item
                        name="nombre"
                        label="Nombre completo"
                        rules={[{ required: true, message: 'Por favor ingrese el nombre completo' }]}
                    >
                        <Input placeholder="Nombre y apellidos" size="large" />
                    </Form.Item>

                    <Form.Item
                        name="cedula"
                        label="Cédula"
                        rules={[{ required: true, message: 'Por favor ingrese la cédula' }]}
                    >
                        <Input placeholder="Número de cédula" size="large" />
                    </Form.Item>

                    <Form.Item
                        name="cargo"
                        label="Cargo"
                        rules={[{ required: true, message: 'Por favor ingrese el cargo' }]}
                    >
                        <Input placeholder="Cargo del empleado" size="large" />
                    </Form.Item>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="campus_id"
                            label="Campus"
                            rules={[{ required: true, message: 'Por favor seleccione un campus' }]}
                        >
                            <Select placeholder="Seleccione un campus" size="large">
                                {campus.map(c => (
                                    <Option key={c.id} value={c.id}>{c.campus}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="dependencia_id"
                            label="Dependencia"
                            rules={[{ required: true, message: 'Por favor seleccione una dependencia' }]}
                        >
                            <Select placeholder="Seleccione una dependencia" size="large">
                                {dependencias.map(d => (
                                    <Option key={d.id} value={d.id}>{d.dependencia}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Form.Item
                            name="tipo_contrato_id"
                            label="Tipo de Contrato"
                            rules={[{ required: true, message: 'Por favor seleccione un tipo de contrato' }]}
                        >
                            <Select placeholder="Seleccione un tipo de contrato" size="large">
                                {tiposContratos.map(tc => (
                                    <Option key={tc.id} value={tc.id}>{tc.tipo_contrato}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="tipo_empleado_id"
                            label="Tipo de Empleado"
                            rules={[{ required: true, message: 'Por favor seleccione un tipo de empleado' }]}
                        >
                            <Select placeholder="Seleccione un tipo de empleado" size="large">
                                {tiposEmpleados.map(te => (
                                    <Option key={te.id} value={te.id}>{te.tipo_empleado}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="estado"
                            label="Estado del empleado"
                            rules={[{ required: true, message: 'Por favor seleccione un estado' }]}
                        >
                            <Select placeholder="Seleccione un estado" size="large">
                                <Option value="activo">Activo</Option>
                                <Option value="inactivo">Inactivo</Option>
                            </Select>
                        </Form.Item>
                    </div>
                </Form>
            )}
        </Modal>
    );
};

export default EmpleadoFormModal;